import { useContext, useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Switch,
  Divider,
  Avatar,
  Chip,
  alpha,
  RadioGroup,
  Radio,
  FormControlLabel,
  Slider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AnimationOutlinedIcon from '@mui/icons-material/AnimationOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import TabOutlinedIcon from '@mui/icons-material/TabOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimations } from '../../contexts/AnimationsContext';
import { useMenuLayout, MenuLayout } from '../../contexts/MenuLayoutContext';
import { useZoom, ZOOM_LEVELS } from '../../contexts/ZoomContext';
import { LanguageContext } from '../../contexts/LanguageContext';
import { SupportedLanguage } from '../../i18n';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { getUser, setUser, hasRole } from '../../utils/auth';
import { useLocations } from '../../hooks/useUserLocations';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import { useTierAccess } from '../../hooks/useTierAccess';
import { UsageIndicator } from '../../components/subscription/UsageIndicator';

export const Settings = () => {
  const { mode } = useContext(ColorModeContext);
  const { animationsEnabled, toggleAnimations } = useAnimations();
  const { layout, setLayout } = useMenuLayout();
  const { zoomLevel, setZoomLevel, zoomIn, zoomOut, resetZoom } = useZoom();
  const { language, setLanguage, languageNames, supportedLanguages } = useContext(LanguageContext);
  const animations = useAnimationStyles();
  const user = getUser();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const { limits, isLoading: isLoadingTierLimits } = useTierAccess();
  const { t } = useTranslation();

  // Tax rate settings — owner / org_owner (or a super admin) only.
  //
  // This MUST mirror AdminGuard on PATCH /organizations/:id/tax-rate, which requires
  // the JWT's `isAdmin` (auth.service.ts: isSuperAdmin || roles.some(r => r.isAdmin)).
  // Only `owner` and `org_owner` are seeded isAdmin: true; `org_admin` is explicitly
  // isAdmin: false (backend/src/database/seeds/data/permissions.data.ts), so gating on
  // org_admin here would show a control whose every save 403s.
  const canEditTaxRate = Boolean(user?.isSuperAdmin) || hasRole(['owner', 'org_owner']);
  const organizationId = user?.organizationId;
  const [taxRateValue, setTaxRateValue] = useState<string>('');

  const { data: fetchedTaxRate } = useQuery({
    queryKey: ['organizationTaxRate', organizationId],
    queryFn: () =>
      axiosInstance
        .get(`/organizations/${organizationId}/tax-rate`)
        .then((res) => res.data.taxRate),
    enabled: canEditTaxRate && !!organizationId,
  });

  // Sync local editable tax rate value when fetched value changes
  useEffect(() => {
    if (fetchedTaxRate !== undefined && fetchedTaxRate !== null) {
      setTaxRateValue(String(fetchedTaxRate));
    }
  }, [fetchedTaxRate]);

  const updateTaxRateMutation = useMutation({
    mutationFn: async (taxRate: number) => {
      const response = await axiosInstance.patch(`/organizations/${organizationId}/tax-rate`, { taxRate });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationTaxRate', organizationId] });
      showMessage(t('settings.taxRate.saveSuccess'), 'success');
    },
    onError: (error: any) => {
      showMessage(error?.response?.data?.message || t('settings.taxRate.saveError'), 'error');
    },
  });

  const handleSaveTaxRate = () => {
    updateTaxRateMutation.mutate(Number(taxRateValue));
  };

  // Default location state and data
  const [selectedDefaultLocation, setSelectedDefaultLocation] = useState<string>(
    user?.defaultLocationId || ''
  );
  const { data: locations, isLoading: isLoadingLocations } = useLocations();

  // Update selected location when user data changes
  useEffect(() => {
    if (user?.defaultLocationId) {
      setSelectedDefaultLocation(user.defaultLocationId);
    }
  }, [user?.defaultLocationId]);

  // Mutation to update default location
  const updateDefaultLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const response = await axiosInstance.patch('/users/me', {
        defaultLocationId: locationId || null,
      });
      return response.data;
    },
    onSuccess: (updatedUser) => {
      // Update local storage with new user data
      if (user) {
        const newUser = { ...user, defaultLocationId: updatedUser.defaultLocationId };
        setUser(newUser);
      }
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      showMessage('Default location updated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(
        error?.response?.data?.message || 'Failed to update default location',
        'error'
      );
      // Reset to previous value
      setSelectedDefaultLocation(user?.defaultLocationId || '');
    },
  });

  const handleDefaultLocationChange = (locationId: string) => {
    setSelectedDefaultLocation(locationId);
  };

  const handleSaveDefaultLocation = () => {
    updateDefaultLocationMutation.mutate(selectedDefaultLocation);
  };

  const hasLocationChanged = selectedDefaultLocation !== (user?.defaultLocationId || '');

  const handleLayoutChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLayout(event.target.value as MenuLayout);
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLanguage(event.target.value as SupportedLanguage);
  };

  const handleZoomChange = (_event: Event, newValue: number | number[]) => {
    setZoomLevel(newValue as number);
  };

  const zoomMarks = ZOOM_LEVELS.filter((_, i) => i % 2 === 0).map((z) => ({
    value: z.value,
    label: z.label,
  }));

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return palette.error;
      case 'org_owner':
        return palette.primary;
      case 'store_owner':
      case 'store_manager':
        return palette.accent;
      case 'warehouse_manager':
        return palette.info;
      default:
        return palette.textMuted;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'org_owner':
        return 'Organization Owner';
      case 'store_owner':
        return 'Store Owner';
      case 'store_manager':
        return 'Store Manager';
      case 'warehouse_manager':
        return 'Warehouse Manager';
      case 'customer':
        return 'Customer';
      default:
        return role;
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SettingsOutlinedIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            SYSTEM CONFIGURATION
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.extrabold,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          SETTINGS
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
          Manage your account preferences and application settings
        </Typography>
      </Box>

      {/* User Information Card */}
      <Card
        sx={{
          ...animations.slideUp(0.2),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonOutlineIcon sx={{ color: palette.primary, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              USER INFORMATION
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Avatar and Role */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: alpha(palette.primary, 0.1),
                border: `2px solid ${palette.primary}`,
                color: palette.primary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize['2xl'],
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {user?.email ? getInitials(user.email) : 'US'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  mb: 1,
                }}
              >
                {user?.email || 'User Account'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(user?.roles?.length ? user.roles : user?.roleSystemKeys?.map((key: string) => ({ systemKey: key })) || [{ systemKey: 'user' }]).map((role: any, index: number) => {
                  const roleKey = role.systemKey || role;
                  return (
                    <Chip
                      key={roleKey + index}
                      label={getRoleLabel(roleKey)}
                      sx={{
                        backgroundColor: alpha(getRoleBadgeColor(roleKey), 0.1),
                        color: getRoleBadgeColor(roleKey),
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        border: `1px solid ${getRoleBadgeColor(roleKey)}`,
                        textTransform: 'uppercase',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: colors.border, mb: 3 }} />

          {/* User Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha(palette.accent, 0.1),
                  border: `1px solid ${palette.accent}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 20, color: palette.accent }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  EMAIL ADDRESS
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                  }}
                >
                  {user?.email || 'Not available'}
                </Typography>
              </Box>
            </Box>

            {/* User ID */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha(palette.info, 0.1),
                  border: `1px solid ${palette.info}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BadgeOutlinedIcon sx={{ fontSize: 20, color: palette.info }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    textTransform: 'uppercase',
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  USER ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                  }}
                >
                  {user?.id || 'Not available'}
                </Typography>
              </Box>
            </Box>

            {/* Location ID (if applicable) */}
            {user?.locationId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: alpha(palette.success, 0.1),
                    border: `1px solid ${palette.success}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <StorefrontOutlinedIcon sx={{ fontSize: 20, color: palette.success }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      fontWeight: typography.fontWeight.bold,
                    }}
                  >
                    LOCATION ID
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textPrimary,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    {user.locationId}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Default Location Selector */}
            {locations && locations.length > 0 && (
              <>
                <Divider sx={{ borderColor: colors.border, my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: alpha(palette.primary, 0.1),
                      border: `1px solid ${palette.primary}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <StorefrontOutlinedIcon sx={{ fontSize: 20, color: palette.primary }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        textTransform: 'uppercase',
                        fontWeight: typography.fontWeight.bold,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      DEFAULT LOCATION FOR POS
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        display: 'block',
                        mb: 2,
                      }}
                    >
                      Select your default location for POS and other location-specific features
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl
                        sx={{
                          minWidth: 250,
                          '& .MuiOutlinedInput-root': {
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.sm,
                            backgroundColor: colors.surfaceElevated,
                            '& fieldset': {
                              borderColor: colors.border,
                            },
                            '&:hover fieldset': {
                              borderColor: palette.primary,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: palette.primary,
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.sm,
                            color: colors.textMuted,
                            '&.Mui-focused': {
                              color: palette.primary,
                            },
                          },
                          '& .MuiSelect-select': {
                            color: colors.textPrimary,
                          },
                        }}
                        size="small"
                      >
                        <InputLabel>Select Location</InputLabel>
                        <Select
                          value={selectedDefaultLocation}
                          onChange={(e) => handleDefaultLocationChange(e.target.value)}
                          label="Select Location"
                          disabled={isLoadingLocations || updateDefaultLocationMutation.isPending}
                        >
                          <MenuItem value="">
                            <em>No default location</em>
                          </MenuItem>
                          {locations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveDefaultLocation}
                        disabled={!hasLocationChanged || updateDefaultLocationMutation.isPending}
                        startIcon={
                          updateDefaultLocationMutation.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveOutlinedIcon />
                          )
                        }
                        sx={{
                          backgroundColor: palette.primary,
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          fontSize: typography.fontSize.xs,
                          textTransform: 'uppercase',
                          px: 2,
                          '&:hover': {
                            backgroundColor: alpha(palette.primary, 0.8),
                          },
                          '&.Mui-disabled': {
                            backgroundColor: colors.border,
                            color: colors.textMuted,
                          },
                        }}
                      >
                        {updateDefaultLocationMutation.isPending ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                    {hasLocationChanged && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: palette.warning,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          display: 'block',
                          mt: 1,
                        }}
                      >
                        You have unsaved changes
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}
            {isLoadingLocations && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Loading locations...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* Tax Rate Card (org_admin / org_owner only) */}
      {canEditTaxRate && (
        <Card
          sx={{
            ...animations.slideUp(0.25),
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 3,
              backgroundColor: colors.surfaceElevated,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ReceiptLongOutlinedIcon sx={{ color: palette.primary, fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  textTransform: 'uppercase',
                  fontSize: typography.fontSize.md,
                  letterSpacing: typography.letterSpacing.wide,
                }}
              >
                {t('settings.taxRate.title')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                mb: 3,
              }}
            >
              {t('settings.taxRate.description')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                type="number"
                label={t('settings.taxRate.label')}
                value={taxRateValue}
                onChange={(e) => setTaxRateValue(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                size="small"
                disabled={updateTaxRateMutation.isPending}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    backgroundColor: colors.surfaceElevated,
                    '& fieldset': {
                      borderColor: colors.border,
                    },
                    '&:hover fieldset': {
                      borderColor: palette.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    color: colors.textMuted,
                    '&.Mui-focused': {
                      color: palette.primary,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: colors.textPrimary,
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveTaxRate}
                disabled={updateTaxRateMutation.isPending || taxRateValue === ''}
                startIcon={
                  updateTaxRateMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveOutlinedIcon />
                  )
                }
                sx={{
                  backgroundColor: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  fontSize: typography.fontSize.xs,
                  textTransform: 'uppercase',
                  px: 2,
                  '&:hover': {
                    backgroundColor: alpha(palette.primary, 0.8),
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.border,
                    color: colors.textMuted,
                  },
                }}
              >
                {updateTaxRateMutation.isPending ? 'Saving...' : t('settings.taxRate.save')}
              </Button>
            </Box>
          </Box>
        </Card>
      )}

      {/* Subscription Usage Card */}
      <Card
        sx={{
          ...animations.slideUp(0.3),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SubscriptionsOutlinedIcon sx={{ color: palette.primary, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              SUBSCRIPTION USAGE
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {isLoadingTierLimits ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Loading subscription usage...
              </Typography>
            </Box>
          ) : limits ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Products Usage */}
              <Box>
                <UsageIndicator
                  limitType="products"
                  current={limits.currentProducts}
                  limit={limits.maxProducts}
                  showLabel={true}
                />
              </Box>

              {/* Locations Usage */}
              <Box>
                <UsageIndicator
                  limitType="locations"
                  current={limits.currentLocations}
                  limit={limits.maxLocations}
                  showLabel={true}
                />
              </Box>

              {/* Sales Usage */}
              <Box>
                <UsageIndicator
                  limitType="sales"
                  current={0}
                  limit={limits.salesRateLimit}
                  showLabel={true}
                />
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Unable to load subscription usage information
            </Typography>
          )}
        </Box>
      </Card>

      {/* Appearance Settings Card */}
      <Card
        sx={{
          ...animations.slideUp(0.4),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AnimationOutlinedIcon sx={{ color: palette.accent, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              APPEARANCE
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Animation Toggle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
              borderRadius: theme.radius.xs,
              transition: theme.transitions.fast,
              '&:hover': {
                borderColor: palette.accent,
                backgroundColor: alpha(palette.accent, 0.05),
              },
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AnimationOutlinedIcon sx={{ fontSize: 18, color: palette.accent }} />
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    fontSize: typography.fontSize.sm,
                  }}
                >
                  Enable Animations
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  display: 'block',
                }}
              >
                Toggle smooth entrance and transition animations throughout the app
              </Typography>
            </Box>
            <Switch
              checked={animationsEnabled}
              onChange={toggleAnimations}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: palette.accent,
                  '&:hover': {
                    backgroundColor: alpha(palette.accent, 0.1),
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: palette.accent,
                },
                '& .MuiSwitch-track': {
                  backgroundColor: colors.border,
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 3, borderColor: colors.border }} />

          {/* Zoom Control */}
          <Box
            sx={{
              p: 2,
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
              borderRadius: theme.radius.xs,
              transition: theme.transitions.fast,
              '&:hover': {
                borderColor: palette.accent,
                backgroundColor: alpha(palette.accent, 0.05),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <ZoomInOutlinedIcon sx={{ fontSize: 18, color: palette.accent }} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                      fontSize: typography.fontSize.sm,
                    }}
                  >
                    Interface Zoom
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textSecondary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    display: 'block',
                  }}
                >
                  Scale the entire interface up or down
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: theme.radius.xs,
                  px: 1,
                  py: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={zoomOut}
                  disabled={zoomLevel <= 0.8}
                  sx={{
                    color: colors.textSecondary,
                    '&:hover': { color: palette.accent },
                    '&.Mui-disabled': { color: colors.textMuted },
                  }}
                >
                  <ZoomOutOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.bold,
                    color: palette.accent,
                    minWidth: 48,
                    textAlign: 'center',
                  }}
                >
                  {Math.round(zoomLevel * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={zoomIn}
                  disabled={zoomLevel >= 1.2}
                  sx={{
                    color: colors.textSecondary,
                    '&:hover': { color: palette.accent },
                    '&.Mui-disabled': { color: colors.textMuted },
                  }}
                >
                  <ZoomInOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: colors.border }} />
                <IconButton
                  size="small"
                  onClick={resetZoom}
                  disabled={zoomLevel === 1.0}
                  sx={{
                    color: colors.textSecondary,
                    '&:hover': { color: palette.warning },
                    '&.Mui-disabled': { color: colors.textMuted },
                  }}
                >
                  <RestartAltOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Zoom Slider */}
            <Box sx={{ px: 1 }}>
              <Slider
                value={zoomLevel}
                onChange={handleZoomChange}
                min={0.8}
                max={1.2}
                step={0.05}
                marks={zoomMarks}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                sx={{
                  color: palette.accent,
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: palette.accent,
                    border: `2px solid ${colors.surface}`,
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: `0 0 0 8px ${alpha(palette.accent, 0.2)}`,
                    },
                  },
                  '& .MuiSlider-track': {
                    height: 4,
                    borderRadius: 2,
                  },
                  '& .MuiSlider-rail': {
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: colors.border,
                  },
                  '& .MuiSlider-mark': {
                    width: 2,
                    height: 8,
                    backgroundColor: colors.border,
                  },
                  '& .MuiSlider-markActive': {
                    backgroundColor: palette.accent,
                  },
                  '& .MuiSlider-markLabel': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    color: colors.textMuted,
                  },
                  '& .MuiSlider-valueLabel': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${palette.accent}`,
                    borderRadius: theme.radius.xs,
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: alpha(
                animationsEnabled ? palette.success : palette.warning,
                0.1
              ),
              border: `1px solid ${animationsEnabled ? palette.success : palette.warning}`,
              borderRadius: theme.radius.xs,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: animationsEnabled ? palette.success : palette.warning,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
              }}
            >
              {animationsEnabled
                ? '✓ Animations are enabled - Enjoy smooth transitions'
                : '⚠ Animations are disabled - Reduced motion mode active'}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Layout Preferences Card */}
      <Card
        sx={{
          ...animations.slideUp(0.6),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          mt: 3,
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DashboardCustomizeOutlinedIcon sx={{ color: palette.info, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              LAYOUT PREFERENCES
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              mb: 2,
            }}
          >
            Choose how the navigation menu is displayed
          </Typography>

          <RadioGroup value={layout} onChange={handleLayoutChange}>
            {/* Flat Mode Option */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 2,
                mb: 2,
                backgroundColor: layout === 'flat' ? alpha(palette.info, 0.1) : colors.surfaceElevated,
                border: `1px solid ${layout === 'flat' ? palette.info : colors.border}`,
                borderRadius: theme.radius.xs,
                transition: theme.transitions.fast,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: palette.info,
                  backgroundColor: alpha(palette.info, 0.05),
                },
              }}
              onClick={() => setLayout('flat')}
            >
              <FormControlLabel
                value="flat"
                control={
                  <Radio
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': {
                        color: palette.info,
                      },
                    }}
                  />
                }
                label=""
                sx={{ m: 0, mr: 1 }}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha(palette.info, 0.1),
                  border: `1px solid ${palette.info}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <ViewListOutlinedIcon sx={{ fontSize: 20, color: palette.info }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    fontSize: typography.fontSize.sm,
                    mb: 0.5,
                  }}
                >
                  Flat Layout
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textSecondary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    display: 'block',
                  }}
                >
                  Expanded sidebar showing all menu sections and items. Click any item to navigate directly.
                </Typography>
              </Box>
            </Box>

            {/* Tabs Mode Option */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 2,
                backgroundColor: layout === 'tabs' ? alpha(palette.info, 0.1) : colors.surfaceElevated,
                border: `1px solid ${layout === 'tabs' ? palette.info : colors.border}`,
                borderRadius: theme.radius.xs,
                transition: theme.transitions.fast,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: palette.info,
                  backgroundColor: alpha(palette.info, 0.05),
                },
              }}
              onClick={() => setLayout('tabs')}
            >
              <FormControlLabel
                value="tabs"
                control={
                  <Radio
                    sx={{
                      color: colors.textMuted,
                      '&.Mui-checked': {
                        color: palette.info,
                      },
                    }}
                  />
                }
                label=""
                sx={{ m: 0, mr: 1 }}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha(palette.info, 0.1),
                  border: `1px solid ${palette.info}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <TabOutlinedIcon sx={{ fontSize: 20, color: palette.info }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    fontSize: typography.fontSize.sm,
                    mb: 0.5,
                  }}
                >
                  Tabs Layout
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.textSecondary,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    display: 'block',
                  }}
                >
                  Compact sidebar with section headers only. Click a section to see its items as horizontal tabs.
                </Typography>
              </Box>
            </Box>
          </RadioGroup>

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: alpha(palette.info, 0.1),
              border: `1px solid ${palette.info}`,
              borderRadius: theme.radius.xs,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: palette.info,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
              }}
            >
              {layout === 'flat'
                ? '✓ Flat layout active - Full navigation visible in sidebar'
                : '✓ Tabs layout active - Compact sidebar with section tabs'}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Language Preferences Card */}
      <Card
        sx={{
          ...animations.slideUp(0.8),
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          mt: 3,
        }}
      >
        <Box
          sx={{
            p: 3,
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TranslateOutlinedIcon sx={{ color: palette.success, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              LANGUAGE PREFERENCES
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              mb: 2,
            }}
          >
            Choose your preferred language for the interface
          </Typography>

          <RadioGroup value={language} onChange={handleLanguageChange}>
            {supportedLanguages.map((lang) => (
              <Box
                key={lang}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: 2,
                  mb: 2,
                  backgroundColor: language === lang ? alpha(palette.success, 0.1) : colors.surfaceElevated,
                  border: `1px solid ${language === lang ? palette.success : colors.border}`,
                  borderRadius: theme.radius.xs,
                  transition: theme.transitions.fast,
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: palette.success,
                    backgroundColor: alpha(palette.success, 0.05),
                  },
                  '&:last-child': {
                    mb: 0,
                  },
                }}
                onClick={() => setLanguage(lang)}
              >
                <FormControlLabel
                  value={lang}
                  control={
                    <Radio
                      sx={{
                        color: colors.textMuted,
                        '&.Mui-checked': {
                          color: palette.success,
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0, mr: 1 }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: alpha(palette.success, 0.1),
                    border: `1px solid ${palette.success}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      color: palette.success,
                      textTransform: 'uppercase',
                    }}
                  >
                    {lang.toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                      fontSize: typography.fontSize.sm,
                      mb: 0.5,
                    }}
                  >
                    {languageNames[lang]}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textSecondary,
                      fontFamily: typography.fontFamily.mono,
                      fontSize: typography.fontSize.xs,
                      display: 'block',
                    }}
                  >
                    {lang === 'en' && 'English - Left to right'}
                    {lang === 'fr' && 'French - Left to right'}
                    {lang === 'ar' && 'Arabic - Right to left'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: alpha(palette.success, 0.1),
              border: `1px solid ${palette.success}`,
              borderRadius: theme.radius.xs,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: palette.success,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
              }}
            >
              ✓ {languageNames[language]} selected - Interface will update immediately
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
